import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';

interface VerificationState {
  email: string;
  phone: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    emergency_pin: string;
  };
}

const Verify: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as VerificationState;

  const [emailCode, setEmailCode] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Redirect if no state
  useEffect(() => {
    if (!state?.email || !state?.phone) {
      navigate('/register');
    }
  }, [state, navigate]);

  const verifyEmail = async () => {
    if (!emailCode) return;
    
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: emailCode }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al verificar email');
      }

      setEmailVerified(true);
      setSuccess('Email verificado exitosamente');
      
      // Check if both are verified
      if (phoneVerified) {
        setTimeout(() => navigate('/onboarding'), 1500);
      }

    } catch (error: any) {
      setError(error.message || 'Error al verificar email');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPhone = async () => {
    if (!phoneCode || phoneCode.length !== 6) return;
    
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/verify-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: phoneCode }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al verificar teléfono');
      }

      setPhoneVerified(true);
      setSuccess('Teléfono verificado exitosamente');
      
      // Check if both are verified
      if (emailVerified) {
        setTimeout(() => navigate('/onboarding'), 1500);
      }

    } catch (error: any) {
      setError(error.message || 'Error al verificar teléfono');
    } finally {
      setIsLoading(false);
    }
  };

  const resendVerification = async (type: 'email' | 'phone') => {
    setIsResending(true);
    setError('');

    try {
      const endpoint = type === 'email' ? 'resend-email' : 'resend-phone';
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/auth/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al reenviar código');
      }

      setSuccess(`Código reenviado a tu ${type === 'email' ? 'email' : 'teléfono'}`);

    } catch (error: any) {
      setError(error.message || 'Error al reenviar código');
    } finally {
      setIsResending(false);
    }
  };

  const skipToOnboarding = () => {
    // Allow users to continue without full verification for demo purposes
    navigate('/onboarding');
  };

  if (!state) {
    return null;
  }

  const allVerified = emailVerified && phoneVerified;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%', m: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <HospitalIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Verificación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verifica tu email y teléfono para continuar
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>¡Hola {state.user.first_name}!</strong><br />
              Tu cuenta ha sido creada exitosamente. Para mayor seguridad, necesitamos verificar tu email y teléfono.
            </Typography>
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Email Verification */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <EmailIcon sx={{ mr: 1, color: emailVerified ? 'success.main' : 'action.active' }} />
                  <Typography variant="h6">
                    Verificar Email
                  </Typography>
                  {emailVerified && <CheckIcon sx={{ ml: 'auto', color: 'success.main' }} />}
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Enviamos un código a:<br />
                  <strong>{state.email}</strong>
                </Typography>

                {!emailVerified ? (
                  <>
                    <TextField
                      fullWidth
                      label="Código de Email"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value)}
                      placeholder="Ingresa el token recibido"
                      sx={{ mb: 2 }}
                    />
                    
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={verifyEmail}
                      disabled={isLoading || !emailCode}
                      sx={{ mb: 1 }}
                    >
                      Verificar Email
                    </Button>
                    
                    <Button
                      fullWidth
                      variant="text"
                      startIcon={<RefreshIcon />}
                      onClick={() => resendVerification('email')}
                      disabled={isResending}
                      size="small"
                    >
                      Reenviar código
                    </Button>
                  </>
                ) : (
                  <Alert severity="success">
                    Email verificado correctamente
                  </Alert>
                )}
              </Paper>
            </Grid>

            {/* Phone Verification */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PhoneIcon sx={{ mr: 1, color: phoneVerified ? 'success.main' : 'action.active' }} />
                  <Typography variant="h6">
                    Verificar Teléfono
                  </Typography>
                  {phoneVerified && <CheckIcon sx={{ ml: 'auto', color: 'success.main' }} />}
                </Box>
                
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Enviamos un SMS a:<br />
                  <strong>{state.phone}</strong>
                </Typography>

                {!phoneVerified ? (
                  <>
                    <TextField
                      fullWidth
                      label="Código SMS"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      inputProps={{ maxLength: 6 }}
                      sx={{ mb: 2 }}
                    />
                    
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={verifyPhone}
                      disabled={isLoading || phoneCode.length !== 6}
                      sx={{ mb: 1 }}
                    >
                      Verificar Teléfono
                    </Button>
                    
                    <Button
                      fullWidth
                      variant="text"
                      startIcon={<RefreshIcon />}
                      onClick={() => resendVerification('phone')}
                      disabled={isResending}
                      size="small"
                    >
                      Reenviar SMS
                    </Button>
                  </>
                ) : (
                  <Alert severity="success">
                    Teléfono verificado correctamente
                  </Alert>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Emergency PIN Info */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
            <Typography variant="h6" gutterBottom>
              🔐 Tu PIN de Emergencia
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1.2em', fontWeight: 'bold' }}>
              {state.user.emergency_pin}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Guarda este PIN de forma segura. Te permitirá acceso rápido en situaciones de emergencia usando solo tu teléfono.
            </Typography>
          </Paper>

          {/* Continue Button */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {allVerified ? (
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/onboarding')}
                sx={{ minWidth: 200 }}
              >
                Continuar al Perfil Médico
              </Button>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  ¿Tienes problemas con la verificación?
                </Typography>
                <Button
                  variant="outlined"
                  onClick={skipToOnboarding}
                  size="small"
                >
                  Continuar sin verificar (Demo)
                </Button>
              </>
            )}
          </Box>

          <Box mt={4} textAlign="center">
            <Typography variant="caption" color="text.secondary">
              La verificación es importante para tu seguridad y para asegurar que podamos contactarte en emergencias.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Verify;